/**
 * Bridge Game Store - Zustand state management
 */

import { create } from 'zustand';
import {
  dealCards,
  sortHand,
  POSITIONS,
  PARTNERSHIPS,
  SPECIAL_BIDS,
  BID_STRAINS,
  determineTrickWinner,
  calculateScore
} from './bridgeConstants';
import { getAIBid, getAIPlay } from './bridgeAI';

const INITIAL_STATE = {
  // Game setup
  gamePhase: 'setup', // setup, bidding, playing, scoring, gameOver
  hands: { SOUTH: [], WEST: [], NORTH: [], EAST: [] },
  dealer: 'SOUTH',
  vulnerability: { NS: false, EW: false },
  
  // Bidding
  auction: [],
  currentBidder: 'SOUTH',
  contract: null,
  declarer: null,
  dummy: null,
  passCount: 0,
  
  // Play
  currentTrick: [],
  trickNumber: 0,
  tricksWon: { NS: 0, EW: 0 },
  currentPlayer: null,
  trickHistory: [],
  lastTrickWinner: null,  // Track who won the last trick
  showWinEffect: false,   // Show win animation
  
  // Scoring
  score: { NS: 0, EW: 0 },
  gameHistory: [],
  
  // UI
  selectedCard: null,
  showDummy: false,
  message: '',
  isAnimating: false,
  playLock: false,  // Prevents rapid card clicks before state propagates
  
  // New UX Features
  sortMode: 'suit',  // 'suit' or 'rank' - how to sort player's hand
  showTutorial: true,  // Show play phase tutorial on first game
  
  // Step Mode - pause after each action for player to review
  stepMode: false,
  waitingForContinue: false,
  lastAction: null,  // { type: 'bid'|'play', player: string, detail: string }
  
  // Auto-play dummy's obvious cards
  autoPlayDummy: true
};

const useBridgeStore = create((set, get) => ({
  ...INITIAL_STATE,
  
  // Start a new game
  startGame: () => {
    const hands = dealCards();
    set({
      ...INITIAL_STATE,
      gamePhase: 'bidding',
      hands,
      dealer: 'SOUTH',
      currentBidder: 'SOUTH',
      message: 'Bidding phase - Make your bid'
    });
  },
  
  // Start next hand - keeps the running score
  startNextHand: () => {
    const state = get();
    const currentScore = state.score;
    const hands = dealCards();
    
    // Rotate dealer for next hand
    const dealerOrder = ['SOUTH', 'WEST', 'NORTH', 'EAST'];
    const currentDealerIndex = dealerOrder.indexOf(state.dealer);
    const nextDealer = dealerOrder[(currentDealerIndex + 1) % 4];
    
    set({
      ...INITIAL_STATE,
      score: currentScore, // Preserve the running score
      gamePhase: 'bidding',
      hands,
      dealer: nextDealer,
      currentBidder: nextDealer,
      message: `Bidding phase - ${nextDealer === 'SOUTH' ? 'Make your bid' : `${nextDealer} to bid`}`
    });
  },
  
  // Make a bid
  makeBid: (bid) => {
    const state = get();
    if (state.gamePhase !== 'bidding') return;
    if (state.currentBidder !== 'SOUTH') return; // Only player can bid manually
    
    get().processBid('SOUTH', bid);
  },
  
  // Process a bid (for both player and AI)
  processBid: (bidder, bid) => {
    const state = get();
    const newAuction = [...state.auction, { player: bidder, bid }];
    
    // Count consecutive passes
    let passCount = 0;
    for (let i = newAuction.length - 1; i >= 0; i--) {
      if (newAuction[i].bid === SPECIAL_BIDS.PASS) {
        passCount++;
      } else {
        break;
      }
    }
    
    // Update contract if it's a regular bid
    let newContract = state.contract;
    let newDeclarer = state.declarer;
    
    if (bid !== SPECIAL_BIDS.PASS && bid !== SPECIAL_BIDS.DOUBLE && bid !== SPECIAL_BIDS.REDOUBLE) {
      newContract = { ...bid, doubled: false, redoubled: false };
      newDeclarer = bidder;
      
      // Check if partner bid this strain first
      const partnerPos = PARTNERSHIPS.NS.includes(bidder) 
        ? PARTNERSHIPS.NS.find(p => p !== bidder)
        : PARTNERSHIPS.EW.find(p => p !== bidder);
      
      for (const entry of newAuction) {
        if (entry.player === partnerPos && 
            entry.bid !== SPECIAL_BIDS.PASS && 
            entry.bid.strain === bid.strain) {
          newDeclarer = partnerPos;
          break;
        }
      }
    } else if (bid === SPECIAL_BIDS.DOUBLE && newContract) {
      newContract = { ...newContract, doubled: true };
    } else if (bid === SPECIAL_BIDS.REDOUBLE && newContract) {
      newContract = { ...newContract, redoubled: true };
    }
    
    // Check if bidding is complete (3 passes after a bid, or 4 passes total)
    const hasBid = newAuction.some(a => 
      a.bid !== SPECIAL_BIDS.PASS && 
      a.bid !== SPECIAL_BIDS.DOUBLE && 
      a.bid !== SPECIAL_BIDS.REDOUBLE
    );
    
    const biddingComplete = (hasBid && passCount >= 3) || (!hasBid && passCount >= 4);
    
    // Next bidder
    const currentIndex = POSITIONS.indexOf(bidder);
    const nextBidder = POSITIONS[(currentIndex + 1) % 4];
    
    if (biddingComplete) {
      if (!hasBid) {
        // All passed - redeal
        set({ message: 'All passed - Redealing...' });
        setTimeout(() => get().startGame(), 1500);
        return;
      }
      
      // Start play phase
      const dummy = PARTNERSHIPS.NS.includes(newDeclarer)
        ? PARTNERSHIPS.NS.find(p => p !== newDeclarer)
        : PARTNERSHIPS.EW.find(p => p !== newDeclarer);
      
      // Leader is to the left of declarer
      const declarerIndex = POSITIONS.indexOf(newDeclarer);
      const leader = POSITIONS[(declarerIndex + 1) % 4];
      
      set({
        auction: newAuction,
        contract: newContract,
        declarer: newDeclarer,
        dummy,
        passCount,
        gamePhase: 'playing',
        currentPlayer: leader,
        showDummy: true,
        trickNumber: 1,
        message: `Contract: ${newContract.level}${getStrainSymbol(newContract.strain)} by ${newDeclarer}. ${leader} leads.`
      });
      
      // If AI leads, trigger AI play (unless waiting in step mode)
      if (leader !== 'SOUTH' && !get().waitingForContinue) {
        setTimeout(() => get().triggerAIPlay(), 800);
      }
    } else {
      set({
        auction: newAuction,
        contract: newContract,
        declarer: newDeclarer,
        currentBidder: nextBidder,
        passCount,
        message: `${nextBidder}'s bid`
      });
      
      // Trigger AI bid if next bidder is AI (and not waiting in step mode)
      if (nextBidder !== 'SOUTH' && !get().waitingForContinue) {
        setTimeout(() => get().triggerAIBid(), 600);
      }
    }
  },
  
  // Trigger AI bidding
  triggerAIBid: () => {
    const state = get();
    if (state.gamePhase !== 'bidding') return;
    if (state.currentBidder === 'SOUTH') return;
    if (state.waitingForContinue) return; // Wait for player to continue
    
    const aiHand = state.hands[state.currentBidder];
    
    // Get partner's last bid
    let partnerBid = null;
    const partnerPos = state.currentBidder === 'NORTH' ? 'SOUTH' 
      : state.currentBidder === 'SOUTH' ? 'NORTH'
      : state.currentBidder === 'EAST' ? 'WEST' : 'EAST';
    
    for (let i = state.auction.length - 1; i >= 0; i--) {
      if (state.auction[i].player === partnerPos) {
        partnerBid = state.auction[i];
        break;
      }
    }
    
    const aiBidResult = getAIBid(aiHand, state.auction, state.currentBidder, partnerBid);
    
    // If step mode is on, pause after AI bid
    if (state.stepMode) {
      const bidText = aiBidResult.bid === 'PASS' ? 'Pass' : 
        `${aiBidResult.bid.level}${getStrainSymbol(aiBidResult.bid.strain)}`;
      set({
        waitingForContinue: true,
        lastAction: {
          type: 'bid',
          player: state.currentBidder,
          detail: bidText
        }
      });
      // Process the bid but wait for continue before next AI action
      get().processBid(state.currentBidder, aiBidResult.bid);
    } else {
      get().processBid(state.currentBidder, aiBidResult.bid);
    }
  },
  
  // Play a card
  playCard: (card) => {
    const state = get();
    if (state.gamePhase !== 'playing') return;
    if (state.playLock) return;  // Prevent rapid clicks
    if (state.isAnimating) return;
    
    // Check if it's player's turn (or playing dummy's cards as declarer)
    const isPlayerTurn = state.currentPlayer === 'SOUTH' ||
      (state.declarer === 'SOUTH' && state.currentPlayer === state.dummy);
    
    if (!isPlayerTurn) return;
    
    // Validate the play
    const handToPlay = state.currentPlayer === state.dummy && state.declarer === 'SOUTH'
      ? state.hands[state.dummy]
      : state.hands['SOUTH'];
    
    if (!handToPlay.some(c => c.id === card.id)) return;
    
    // Check if must follow suit
    if (state.currentTrick.length > 0) {
      const leadSuit = state.currentTrick[0].card.suit;
      const suitCards = handToPlay.filter(c => c.suit === leadSuit);
      if (suitCards.length > 0 && card.suit !== leadSuit) {
        set({ message: 'Must follow suit!' });
        return;
      }
    }
    
    // Lock immediately to prevent double-clicks
    set({ playLock: true });
    get().processCardPlay(state.currentPlayer, card);
  },
  
  // Process a card being played
  processCardPlay: (player, card) => {
    const state = get();
    
    // Remove card from hand
    const newHands = { ...state.hands };
    newHands[player] = newHands[player].filter(c => c.id !== card.id);
    
    // Add to current trick
    const newTrick = [...state.currentTrick, { player, card }];
    
    // Check if trick is complete
    if (newTrick.length === 4) {
      // Determine winner
      const trumpSuit = state.contract.strain === 'NOTRUMP' ? null : state.contract.strain;
      const { winner } = determineTrickWinner(newTrick, trumpSuit);
      
      // Update tricks won
      const winnerPartnership = PARTNERSHIPS.NS.includes(winner) ? 'NS' : 'EW';
      const newTricksWon = { ...state.tricksWon };
      newTricksWon[winnerPartnership]++;
      
      const newTrickHistory = [...state.trickHistory, { trick: newTrick, winner }];
      
      // Show win effect
      set({
        hands: newHands,
        currentTrick: newTrick,
        lastTrickWinner: winner,
        showWinEffect: true,
        message: `${winner} wins the trick!`,
        isAnimating: true
      });
      
      // After delay, clear trick and continue
      setTimeout(() => {
        const nextTrickNumber = state.trickNumber + 1;
        
        if (nextTrickNumber > 13) {
          // Hand is complete - score it
          set({ showWinEffect: false });
          get().scoreHand(newTricksWon, newTrickHistory);
        } else {
          // Check if human will play next
          const isHumanNext = winner === 'SOUTH' || 
            (winner === state.dummy && state.declarer === 'SOUTH');
          
          set({
            currentTrick: [],
            trickNumber: nextTrickNumber,
            tricksWon: newTricksWon,
            trickHistory: newTrickHistory,
            currentPlayer: winner,
            isAnimating: false,
            showWinEffect: false,
            lastTrickWinner: null,
            playLock: !isHumanNext,  // Unlock if human's turn
            message: `Trick ${nextTrickNumber} - ${winner} leads`
          });
          
          // Trigger AI if winner is AI (unless waiting in step mode)
          if (!isHumanNext && !get().waitingForContinue) {
            setTimeout(() => get().triggerAIPlay(), 500);
          }
          
          // Check for auto-play if dummy leads and only has one card
          if (winner === state.dummy && state.declarer === 'SOUTH' && get().autoPlayDummy) {
            setTimeout(() => get().checkAutoPlayDummy(), 300);
          }
        }
      }, 1500);  // Slightly longer to show win effect
    } else {
      // Trick continues - next player
      const currentIndex = POSITIONS.indexOf(player);
      const nextPlayer = POSITIONS[(currentIndex + 1) % 4];
      
      // Check if human plays next
      const isNextPlayerHuman = nextPlayer === 'SOUTH' || 
        (nextPlayer === state.dummy && state.declarer === 'SOUTH');
      
      set({
        hands: newHands,
        currentTrick: newTrick,
        currentPlayer: nextPlayer,
        selectedCard: null,
        playLock: !isNextPlayerHuman,  // Unlock if human's turn
        message: `${nextPlayer}'s turn`
      });
      
      // Trigger AI play if next player is AI (unless waiting in step mode)
      if (!isNextPlayerHuman && !get().waitingForContinue) {
        setTimeout(() => get().triggerAIPlay(), 500);
      }
      
      // Check for auto-play dummy if it's dummy's turn and declarer is SOUTH
      if (nextPlayer === state.dummy && state.declarer === 'SOUTH' && get().autoPlayDummy) {
        setTimeout(() => get().checkAutoPlayDummy(), 300);
      }
    }
  },
  
  // Check if dummy has only one legal card and auto-play it
  checkAutoPlayDummy: () => {
    const state = get();
    if (state.gamePhase !== 'playing') return;
    if (state.currentPlayer !== state.dummy) return;
    if (state.declarer !== 'SOUTH') return;
    if (!state.autoPlayDummy) return;
    
    const dummyHand = state.hands[state.dummy];
    if (!dummyHand || dummyHand.length === 0) return;
    
    // If only one card left, auto-play it
    if (dummyHand.length === 1) {
      get().playCard(dummyHand[0]);
      return;
    }
    
    // If must follow suit and only one card of that suit, auto-play it
    if (state.currentTrick.length > 0) {
      const leadSuit = state.currentTrick[0].card.suit;
      const suitCards = dummyHand.filter(c => c.suit === leadSuit);
      
      if (suitCards.length === 1) {
        // Only one card of the led suit - must play it
        get().playCard(suitCards[0]);
        return;
      }
    }
  },
  
  // Trigger AI to play a card
  triggerAIPlay: () => {
    const state = get();
    if (state.gamePhase !== 'playing') return;
    if (state.isAnimating) return;
    if (state.waitingForContinue) return; // Wait for player to continue
    
    const player = state.currentPlayer;
    
    // Skip if it's player's turn
    if (player === 'SOUTH') return;
    if (player === state.dummy && state.declarer === 'SOUTH') return;
    
    const hand = state.hands[player];
    if (hand.length === 0) return;
    
    const trumpSuit = state.contract.strain === 'NOTRUMP' ? null : state.contract.strain;
    const card = getAIPlay(
      hand,
      state.currentTrick,
      trumpSuit,
      state.contract,
      state.trickHistory,
      player
    );
    
    // If step mode is on, pause after AI plays
    if (state.stepMode) {
      const cardText = `${card.rank}${getSuitSymbol(card.suit)}`;
      set({
        waitingForContinue: true,
        lastAction: {
          type: 'play',
          player: player,
          detail: cardText
        }
      });
    }
    
    get().processCardPlay(player, card);
  },
  
  // Score the completed hand
  scoreHand: (tricksWon, trickHistory) => {
    const state = get();
    const declarerPartnership = PARTNERSHIPS.NS.includes(state.declarer) ? 'NS' : 'EW';
    const declarerTricks = tricksWon[declarerPartnership];
    const vulnerable = state.vulnerability[declarerPartnership];
    
    const points = calculateScore(state.contract, declarerTricks, vulnerable);
    
    const newScore = { ...state.score };
    if (points > 0) {
      newScore[declarerPartnership] += points;
    } else {
      // Penalty points go to opponents
      const oppPartnership = declarerPartnership === 'NS' ? 'EW' : 'NS';
      newScore[oppPartnership] += Math.abs(points);
    }
    
    const tricksNeeded = 6 + state.contract.level;
    const made = declarerTricks >= tricksNeeded;
    
    set({
      gamePhase: 'scoring',
      score: newScore,
      tricksWon,
      trickHistory,
      message: made 
        ? `Contract made! ${state.declarer} took ${declarerTricks} tricks. +${points} points`
        : `Contract failed. ${state.declarer} took ${declarerTricks} tricks (needed ${tricksNeeded}). ${Math.abs(points)} penalty`,
      isAnimating: false
    });
  },
  
  // Select a card
  selectCard: (card) => {
    set({ selectedCard: card });
  },
  
  // Toggle sort mode (suit vs rank)
  toggleSortMode: () => {
    set(state => ({
      sortMode: state.sortMode === 'suit' ? 'rank' : 'suit'
    }));
  },
  
  // Set sort mode explicitly
  setSortMode: (mode) => {
    set({ sortMode: mode });
  },
  
  // Dismiss tutorial
  dismissTutorial: () => {
    set({ showTutorial: false });
  },
  
  // Toggle step mode
  toggleStepMode: () => {
    set(state => ({ stepMode: !state.stepMode }));
  },
  
  // Toggle auto-play dummy
  toggleAutoPlayDummy: () => {
    set(state => ({ autoPlayDummy: !state.autoPlayDummy }));
  },
  
  // Continue from step mode pause
  continueFromPause: () => {
    const state = get();
    set({ waitingForContinue: false, lastAction: null });
    
    // Trigger next AI action if needed
    if (state.gamePhase === 'bidding' && state.currentBidder !== 'SOUTH') {
      setTimeout(() => get().triggerAIBid(), 300);
    } else if (state.gamePhase === 'playing') {
      const player = state.currentPlayer;
      const isHumanTurn = player === 'SOUTH' || 
        (player === state.dummy && state.declarer === 'SOUTH');
      if (!isHumanTurn) {
        setTimeout(() => get().triggerAIPlay(), 300);
      }
    }
  },
  
  // Reset game
  resetGame: () => {
    set(INITIAL_STATE);
  }
}));

// Helper function to get strain symbol
function getStrainSymbol(strain) {
  const symbols = {
    CLUBS: '♣',
    DIAMONDS: '♦',
    HEARTS: '♥',
    SPADES: '♠',
    NOTRUMP: 'NT'
  };
  return symbols[strain] || strain;
}

// Helper function to get suit symbol
function getSuitSymbol(suit) {
  const symbols = {
    CLUBS: '♣',
    DIAMONDS: '♦',
    HEARTS: '♥',
    SPADES: '♠'
  };
  return symbols[suit] || suit;
}

export default useBridgeStore;
